"use client";

import AuthButton from "@/components/common/AuthButton";
import { Image as ImageType } from "@/types/Image";
import { Navigation } from "@/types/Navigation";
import { FetchUserAttributesOutput } from "aws-amplify/auth";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import "./index.scss";

const MobileHeader = ({
  logoImage,
  phoneNumber,
  navigation,
  userAttributes,
}: {
  logoImage: ImageType;
  phoneNumber: string;
  navigation: Navigation;
  userAttributes: FetchUserAttributesOutput | null;
}) => {
  const [isNavigationVisible, setIsNavigationVisible] = useState(false);
  const [primaryLinkThatIsOpened, setPrimaryLinkThatIsOpened] = useState("");

  const navigationLinkNodes = navigation.map((primaryLink) => {
    if (primaryLink.subnavigation && primaryLink.subnavigation.length > 0) {
      const subnavigationNodes = primaryLink.subnavigation.map(
        (secondaryLink) => {
          return (
            <li className="secondary-link" key={secondaryLink.label}>
              <Link
                href={secondaryLink.href}
                onClick={() => {
                  setIsNavigationVisible(false);
                }}
              >
                <span className="label">{secondaryLink.label}</span>
              </Link>
            </li>
          );
        }
      );

      const isOpened = primaryLinkThatIsOpened === primaryLink.label;
      return (
        <li
          className={`primary-link parent ${isOpened ? "opened" : "closed"}`}
          key={primaryLink.label}
        >
          <Link
            href="#"
            onClick={() => {
              if (isOpened) {
                setPrimaryLinkThatIsOpened("");
              } else {
                setPrimaryLinkThatIsOpened(primaryLink.label);
              }
            }}
            aria-haspopup={true}
            aria-expanded={isOpened}
          >
            <span className="label">{primaryLink.label}</span>
          </Link>
          <ul className="subnavigation">{subnavigationNodes}</ul>
        </li>
      );
    } else {
      return (
        <li className="primary-link" key={primaryLink.label}>
          <Link
            href={primaryLink.href}
            onClick={() => {
              setIsNavigationVisible(false);
            }}
          >
            <span className="label">{primaryLink.label}</span>
          </Link>
        </li>
      );
    }
  });

  return (
    <div className="component mobile-header">
      <div className="logo-container">
        <Link
          href="/"
          onClick={() => {
            setIsNavigationVisible(false);
          }}
        >
          <Image
            src={logoImage.src}
            width={logoImage.width}
            height={logoImage.height}
            alt={logoImage.alt}
            priority={true}
            fetchPriority="high"
          />
        </Link>
      </div>

      <div
        className={`hamburger-icon ${
          isNavigationVisible ? "is-opened" : "is-closed"
        }`}
      >
        <button
          onClick={() => setIsNavigationVisible(!isNavigationVisible)}
          aria-expanded={isNavigationVisible}
          aria-label={
            isNavigationVisible ? "Close navigation" : "Open navigation"
          }
        >
          {isNavigationVisible && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          )}
          {!isNavigationVisible && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          )}
        </button>
      </div>

      {isNavigationVisible && (
        <nav aria-label="Main navigation">
          <ul>{navigationLinkNodes}</ul>
        </nav>
      )}

      <div className="context-container">
        <AuthButton
          userAttributes={userAttributes}
          onClick={() => {
            setIsNavigationVisible(false);
          }}
          logoImage={logoImage}
        />
      </div>
    </div>
  );
};

export default MobileHeader;
